import { useState } from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import { Alert, Box, Button, Link, MenuItem, Typography } from '@mui/material'

import useTranslations from '../../../i18n/useTranslations'
import useIsMobile from '../../../hooks/useIsMobile'
import { PRINT_SETTINGS } from '../../../routes'
import Select from '../../../ui/components/select'
import TooltipButton from '../../../ui/components/tooltipButton'
import { ReactComponent as Printer } from '../../../assets/icons/printer.svg'

import ModalLayout from '../layout'

import styles from './styles'

const OptionsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
    preview: PropTypes.node,
  }),
)

const PrinPositionPreviewPropTypes = {
  selectedPosition: PropTypes.func.isRequired,
  changePosition: PropTypes.func.isRequired,
  options: OptionsPropType,
}

const PrintPositionPreview = ({
  selectedPosition,
  changePosition,
  options,
}) => {
  const t = useTranslations()

  const selectedOptionIndex = options.findIndex(
    position => position.value === selectedPosition,
  )

  return (
    <Box sx={styles.printPositionPreview}>
      <Box>{options[selectedOptionIndex].preview}</Box>
      <Box sx={styles.selectWrapper}>
        <Select
          defaultValue={selectedOptionIndex}
          label={
            <Box sx={styles.selectLabel}>
              <span>{t('print-modal:label-print-position')}</span>
              <TooltipButton
                type="primary"
                title={t('print-modal:print-position-tooltip')}
                placement="right"
              />
            </Box>
          }
          onChange={event => {
            changePosition(options[event.target.value].value)
          }}
        >
          {options.map((position, index) => (
            <MenuItem key={position.value} value={index}>
              {position.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  )
}

PrintPositionPreview.propTypes = PrinPositionPreviewPropTypes

const AlertPrintModalPropTypes = {
  tagsPerPage: PropTypes.number,
}

const AlertPrintModal = ({ tagsPerPage }) => {
  const t = useTranslations()

  return (
    <Alert
      severity="info"
      iconMapping={{
        info: <Printer />,
      }}
      sx={styles.alert}
    >
      <Typography variant="h4">{t('default-printing-model')}</Typography>
      <Typography component="p" variant="body2">
        {t(`print-settings:model-multi${tagsPerPage}`)} -{' '}
        {t(`print-settings:model-multi${tagsPerPage}-description`)}
      </Typography>
    </Alert>
  )
}

AlertPrintModal.propTypes = AlertPrintModalPropTypes

const PrintModal = ({ onCancel, onConfirm, options, shippingsCount }) => {
  const t = useTranslations()

  const isMobile = useIsMobile()

  const [selected, setSelected] = useState(1)

  return (
    <ModalLayout
      isOpen
      title={t('choose-position-print-labels')}
      content={t('your-are-going-to-print-number-labels', { shippingsCount })}
      extraContent={
        <>
          <AlertPrintModal tagsPerPage={options.length} />
          <Typography variant="body2" color="neutral.main" mt={1}>
            {t('change-model-size-on-print-settings', {
              link: text => (
                <Link
                  to={PRINT_SETTINGS.path}
                  sx={styles.link}
                  component={NavLink}
                >
                  <strong>{text}</strong>
                </Link>
              ),
            })}
          </Typography>
          <PrintPositionPreview
            selectedPosition={selected}
            changePosition={setSelected}
            options={options}
          />
        </>
      }
      onClose={onCancel}
      sx={styles.modal}
      actions={
        <>
          <Button
            type="button"
            size={isMobile ? 'medium' : 'small'}
            variant="outlined"
            onClick={onCancel}
          >
            {t('action:cancel')}
          </Button>
          <Button
            type="submit"
            size={isMobile ? 'medium' : 'small'}
            onClick={() => onConfirm(selected)}
          >
            {t('action:print')}
          </Button>
        </>
      }
    />
  )
}

PrintModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  shippingsCount: PropTypes.number,
  options: OptionsPropType,
}

export default PrintModal
